import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Fab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  Remove as RemoveIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { adminApi } from '../../services/adminApi';

interface User {
  id: number;
  email: string;
}

interface Group {
  id: number;
  name: string;
  users?: User[];
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({ email: '' });
  const [selectedGroup, setSelectedGroup] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching users and groups...');
      const [usersResponse, groupsResponse] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getGroups()
      ]);
      console.log('Users response:', usersResponse);
      console.log('Groups response:', groupsResponse);
      if (usersResponse.success) {
        console.log('Setting users:', usersResponse.data);
        setUsers(usersResponse.data);
      } else {
        console.error('Failed to fetch users:', usersResponse);
        setError('Failed to fetch users');
      }
      if (groupsResponse.success) {
        console.log('Setting groups:', groupsResponse.data);
        setGroups(groupsResponse.data);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({ email: user.email });
    } else {
      setEditingUser(null);
      setFormData({ email: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ email: '' });
  };

  const handleOpenGroupDialog = (user: User) => {
    setSelectedUser(user);
    setSelectedGroup('');
    setOpenGroupDialog(true);
  };

  const handleCloseGroupDialog = () => {
    setOpenGroupDialog(false);
    setSelectedUser(null);
    setSelectedGroup('');
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        // Update user
        const response = await adminApi.updateUser(editingUser.id, formData);
        if (response.success) {
          setUsers(users.map(u => u.id === editingUser.id ? response.data : u));
          handleCloseDialog();
          setSuccess('User updated successfully');
        } else {
          setError('Failed to update user');
        }
      } else {
        // Create user
        const response = await adminApi.createUser(formData);
        if (response.success) {
          setUsers([...users, response.data]);
          handleCloseDialog();
          setSuccess('User created successfully');
        } else {
          setError('Failed to create user');
        }
      }
    } catch (err) {
      setError('Error saving user');
    }
  };

  const handleDelete = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await adminApi.deleteUser(userId);
        if (response.success) {
          setUsers(users.filter(u => u.id !== userId));
          setSuccess('User deleted successfully');
        } else {
          setError('Failed to delete user');
        }
      } catch (err) {
        setError('Error deleting user');
      }
    }
  };

  const handleAssignToGroup = async () => {
    if (!selectedUser || !selectedGroup) {
      setError('Please select both user and group');
      return;
    }

    try {
      const response = await adminApi.addUserToGroup(selectedGroup, selectedUser.id);
      if (response.success) {
        // Refresh data to get updated group information
        await fetchData();
        handleCloseGroupDialog();
        setSuccess('User assigned to group successfully');
      } else {
        setError('Failed to assign user to group');
      }
    } catch (err) {
      setError('Error assigning user to group');
    }
  };

  const handleRemoveFromGroup = async (userId: number, groupId: number) => {
    try {
      const response = await adminApi.removeUserFromGroup(groupId, userId);
      if (response.success) {
        // Refresh data to get updated group information
        await fetchData();
        setSuccess('User removed from group successfully');
      } else {
        setError('Failed to remove user from group');
      }
    } catch (err) {
      setError('Error removing user from group');
    }
  };

  // Navigate to Groups tab and expand specific group
  const handleNavigateToGroup = (groupId: number) => {
    // Store the group ID to expand in sessionStorage
    sessionStorage.setItem('expandGroupId', groupId.toString());
    // Navigate to groups page
    navigate('/groups');
  };

  // Get groups for a specific user
  const getUserGroups = (userId: number) => {
    return groups.filter(group => group.users?.some(user => user.id === userId));
  };

  // Get groups where user is not assigned
  const getAvailableGroups = (userId: number) => {
    return groups.filter(group => !group.users?.some(user => user.id === userId));
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search Filter */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search users by email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
        {searchQuery && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {filteredUsers.length} of {users.length} users
          </Typography>
        )}
      </Box>

      {filteredUsers.length === 0 && searchQuery && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No users found matching "{searchQuery}"
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredUsers.map((user) => {
          const userGroups = getUserGroups(user.id);
          const availableGroups = getAvailableGroups(user.id);
          
          return (
            <Accordion key={user.id} sx={{ borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <EmailIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {user.email}
                  </Typography>
                  <Chip label={`ID: ${user.id}`} size="small" color="primary" variant="outlined" />
                  
                  {/* Group status indicator */}
                  {userGroups.length > 0 && (
                    <Chip 
                      icon={<GroupIcon />} 
                      label={`${userGroups.length} group(s)`} 
                      size="small" 
                      color="success" 
                      variant="outlined" 
                    />
                  )}
                  
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <IconButton
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenGroupDialog(user);
                      }}
                      size="small"
                      title="Assign to Group"
                    >
                      <AssignmentIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(user);
                      }}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(user.id);
                      }}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon color="primary" />
                    Assigned Groups ({userGroups.length})
                  </Typography>
                  
                  {userGroups.length > 0 ? (
                    <List dense>
                      {userGroups.map((group) => (
                        <ListItem key={group.id}>
                          <ListItemText primary={group.name} />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="primary"
                              onClick={() => handleNavigateToGroup(group.id)}
                              size="small"
                              title="View Group Details"
                            >
                              <OpenInNewIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleRemoveFromGroup(user.id, group.id)}
                              size="small"
                            >
                              <RemoveIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This user is not assigned to any groups.
                    </Typography>
                  )}
                  
                  {availableGroups.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Available Groups:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {availableGroups.map((group) => (
                          <Chip
                            key={group.id}
                            label={group.name}
                            size="small"
                            onClick={() => {
                              setSelectedUser(user);
                              setSelectedGroup(group.id);
                              handleAssignToGroup();
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* User Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={(e) => setFormData({ email: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Assignment Dialog */}
      <Dialog open={openGroupDialog} onClose={handleCloseGroupDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign User to Group
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Assigning: <strong>{selectedUser.email}</strong>
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Group</InputLabel>
                <Select
                  value={selectedGroup}
                  label="Select Group"
                  onChange={(e) => setSelectedGroup(e.target.value as number | '')}
                >
                  {getAvailableGroups(selectedUser.id).map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="body2" color="text.secondary">
                Current Groups: {getUserGroups(selectedUser.id).map(g => g.name).join(', ') || 'None'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupDialog}>Cancel</Button>
          <Button 
            onClick={handleAssignToGroup} 
            variant="contained"
            disabled={!selectedGroup}
          >
            Assign to Group
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 